import { checkImportStatusForSessionAsync, ImportStatus, IUploadSessionStatus } from "../../services/upload-service";
import { getCookie } from "../../utils/cookie";

export interface ISessionPoller {
	readonly startSessionPolling: (sessionId: string, expectedFiles?: number) => void;
	readonly getImportedFileIds: () => string[] | null;
	readonly isPolling: () => boolean;
	readonly stopPolling: () => void;
}

export interface ISessionPollerDependencies {
	onFileUploaded?: (amountUploadedFiles: number) => void;
	onPollingFinished?: (failedPollingFileNames: string[], pollingErrors: number) => void;
}

export function createSessionPoller(deps: ISessionPollerDependencies): ISessionPoller {
	let pollSessionCounter = 0;
	let importedFiles: IUploadSessionStatus[];
	let failedFileNames: string[] = [];
	let uploadErrors = 0;
	let polling = false;
	let timeoutId;
	const accountId = getCookie("accid");
	const { onFileUploaded, onPollingFinished } = deps;

	const startSessionPolling = (sessionId: string, expectedFiles?: number) => {
		polling = true;
		pollSessionCounter = 0;
		window.setTimeout(() => pollSession(sessionId, expectedFiles), 1000);
	};

	const pollSession = (sessionId: string, expectedFiles?: number) => {
		pollSessionCounter++;

		checkImportStatusForSessionAsync(sessionId, accountId).then((res) => {
			const finishedImports = res.filter((sessionStatus) => sessionStatus.status !== ImportStatus.Waiting);
			importedFiles = res.filter((importItem) => importItem.status === ImportStatus.Imported);
			const isLoggedOut = res.some((item) => item.status === ImportStatus.LoggedOut);
			if (onFileUploaded) onFileUploaded(importedFiles.length);

			// Sometimes we poll the session without knowing how many files to expect, in that case we keep polling until we get at least 1 file. We should keep polling if the number of files in the session changes
			if (!expectedFiles || (expectedFiles && expectedFiles < res.length)) {
				if (res.length > 0) expectedFiles = res.length;
				else {
					timeoutId = window.setTimeout(() => pollSession(sessionId, expectedFiles), 1000 + pollSessionCounter * 1000);
					return;
				}
			}
			if ((expectedFiles && finishedImports.length === expectedFiles) || isLoggedOut) {
				const failedFiles = res.filter((i) => i.status === ImportStatus.Failed);
				failedFileNames.push(...failedFiles.map((m) => m.originalFileName));
				uploadErrors += failedFiles.length;
				onPollingFinished(failedFileNames, uploadErrors);
				resetUploadErrors();
				polling = false;
			} else {
				timeoutId = window.setTimeout(() => pollSession(sessionId, expectedFiles), 1000 + pollSessionCounter * 1000);
			}
		});
	};

	const resetUploadErrors = () => {
		failedFileNames = [];
		uploadErrors = 0;
	};

	const stopPolling = () => {
		polling = false;
		clearTimeout(timeoutId);
	};

	return {
		startSessionPolling,
		getImportedFileIds: () => importedFiles.map((importedFile) => importedFile.fileId),
		isPolling: () => polling,
		stopPolling,
	} as ISessionPoller;
}
